import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { gDriveOptionsAtom, selectedCourseAtom } from './atoms'
import { GoogleLoginButton } from 'infrastructure/googleDrive'
import React, { ReactNode, useEffect, useState } from 'react'
import {
  Box,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { useGetFileInfo } from '../../components/tree/googleApis/drive'
import { useRetrieveGoogleContent } from './hooks'

export function GDRiveContentLocation() {
  const selected = useAtomValue(selectedCourseAtom)
  if (!selected) return null
  return (
    <GoogleLoginButton
      ParentComponent={GDrivePrompt}
      LoggedInComponent={GDriveContentLocationSelection}
      hideWhenLoggedIn={true}
    />
  )
}

function GDrivePrompt({ children }: { children: ReactNode }) {
  return (
    <Box>
      Authorize Google Drive To Retrieve Source Files
      {children}
    </Box>
  )
}

function GDriveContentLocationSelection() {
  const [allOptions, setAllOptions] = useState<{ id: string; name: string }[]>(
    [],
  )
  const [selectedOption, setSelectedOption] = useState('')
  const options = useAtomValue(gDriveOptionsAtom)
  const [gDriveLocation, setGDriveLocation] = useState('')
  const retrieveGDrive = useRetrieveGoogleContent()
  const [retrieving, setRetrieving] = useState(false)

  useEffect(() => {
    if (!options) return
    setAllOptions(options)
  }, [options])

  if (!options) return <Box>Loading...</Box>

  const onSelect = (e: SelectChangeEvent) => {
    const {
      target: { value },
    } = e
    setSelectedOption(value)
    if (value !== 'userInput') {
      setGDriveLocation(value)
    }
  }

  const renderValue = (value: string) => {
    if (!value || !allOptions) return 'Select GDrive Folder'

    if (value === 'userInput') return 'Enter Below'

    const option = allOptions.find(({ id }) => id === value)
    return option?.name ?? ''
  }

  const onUserInput = ({ name, id }: { name: string; id: string }) => {
    setAllOptions((prev) => [{ id, name }, ...prev])
    setSelectedOption(id)
    setGDriveLocation(id)
  }

  const doRetrieve = async () => {
    setRetrieving(true)
    await retrieveGDrive(gDriveLocation)
    setRetrieving(false)
  }

  return (
    <Box>
      <Box>Select Location Of GDrive Files For Selected Course</Box>
      <Box>
        <Select
          disabled={retrieving}
          displayEmpty={true}
          value={selectedOption}
          onChange={onSelect}
          renderValue={renderValue}
        >
          {allOptions.map((o) => {
            const { name, id } = o
            return (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            )
          })}
          <MenuItem value="userInput">Input Location</MenuItem>
        </Select>
        <Button onClick={doRetrieve} disabled={retrieving || !gDriveLocation}>
          {retrieving ? 'Retrieving' : 'Retrieve'} Files
        </Button>
      </Box>
      {selectedOption === 'userInput' && (
        <UserInputLocation onGot={onUserInput} />
      )}
    </Box>
  )
}

function UserInputLocation({
  onGot,
}: {
  onGot: (p: { id: string; name: string }) => void
}) {
  const [userInput, setUserInput] = useState('')

  const getFile = useGetFileInfo()

  const onGet = async () => {
    const data = await getFile(userInput)
    if (!data || 'error' in data) return
    const { id, name } = data
    if (id && name) {
      setUserInput(name)
      onGot({ id, name })
    }
  }
  return (
    <Box>
      <TextField
        disabled={!getFile}
        value={userInput}
        onChange={({ target: { value } }) => setUserInput(value)}
      />
      <Button onClick={onGet}>Get</Button>
    </Box>
  )
}
